/**
 * SystemPanel Component (New Design System)
 *
 * Redesigned System management panel using the new design system components.
 * This is the most complex panel with billing plans, station moves, and force offsets.
 */

import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography, IconButton } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TuneIcon from '@mui/icons-material/Tune';
import HighlightOff from '@mui/icons-material/HighlightOff';

// Design System Components
import {
    colors,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    ActivityCard,
    ChipField,
    DraftSelectField,
    DraftTextField,
    ReadOnlyField,
    Button,
    StatusBadge,
} from '@components/design-system';

import { useAuditHistoryQuery } from '@features/redux/apis/audit';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import { useSystemMutation, useSystemByIdQuery } from '@features/redux/apis/system';
import { useLocationMapQuery, useNameMapQuery } from '@features/redux/apis/org';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { useQuery } from '@hooks/useQuery';
import { useAlert } from '@hooks/useAlert';
import { OrgChainBreadcrumb } from '../../OrgChainBreadcrumb';
import { KioskSettingsSummary } from '../../KioskSettingsSummary';
import { Selection } from '../../orgNavTypes';
import { SYSTEM_STATUS_OPTIONS, SystemModel, SystemModelMap, SystemVariantMap, SystemStatus } from '@consts/systems';
import { BillingType, UI_SELECTION_MAP, UISelection, WARRANTY_PLAN_MAP, WarrantyType } from '@consts/company';
import { requestPlanChange, requestStationMove, cancelStationMove } from '@api/system';
import { navigateToActivity } from '@utils/activityLinks';
import dayjs from 'dayjs';

// Existing sub-components
import { SystemEffects } from './SystemEffects';
import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';
import { ViewSystemLocation } from './ViewSystemLocation';
import { ForceOffset } from './ForceOffset';
import { BillingPlan } from './BillingPlan/BillingPlan';
import { StationMoveDialog } from './StationMoveDialog';
import { LastConnected } from '@components/LastConnected';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';
import type { System as SystemType } from '@api/system';

// Force Offset Card - wraps ForceOffset with InfoCard to put header actions on same line as title
const ForceOffsetCard: React.FC<{
    serialNumber: string | null | undefined;
    productId?: string | null;
    machineVariant?: string | null;
    stationID?: number | null;
}> = ({ serialNumber, productId, machineVariant, stationID }) => {
    const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);

    return (
        <InfoCard title='Force Offset' icon={<TuneIcon />} actions={headerActions}>
            <ForceOffset
                serialNumber={serialNumber}
                productId={productId}
                machineVariant={machineVariant}
                stationID={stationID}
                hideHeader
                getHeaderActions={setHeaderActions}
            />
        </InfoCard>
    );
};

// Draft state setup
const useSystem = Query.withDraft(
    useSystemByIdQuery,
    {
        StationID: null,
        LocationID: null,
        CompanyID: null,
        ResellerID: null,
        Model: null,
        Hostname: '',
        CreatedDate: '',
        SerialNum: null,
        Status: SystemStatus.Active,
        LastConnected: null,
        ConfigHash: null,
        ProductID: null,
        Notes: '',
        CutPlanID: BillingType.CutFee,
        WarrantyPlanID: WarrantyType.Warranty,
        UISelectionID: UISelection.Standard,
        ProductCodeEntryEnabled: 0,
        AppVersion: null,
        Product: null,
        MachineVariant: null,
        ModelName: 'Unknown',
    },
    (fetched, init) => {
        const next = structuredClone(fetched);
        for (const k in init) {
            const key = k as keyof SystemType;
            if (next[key] === undefined || next[key] === null) {
                (next[key] as any) = init[key];
            }
        }
        return next;
    }
);

export const SystemPanel: React.FC<ManageProps> = ({ operation, onSuccess }) => {
    const stationId = useQuery().get('id')!;
    const auditPk = `Station#${stationId}`;

    const role = useSelector((state: RootState) => state.auth!.Roles.role);
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();

    // Data fetching
    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const { isLoading: locationMapLoading } = useLocationMapQuery({});
    const { data: system, isLoading: systemQueryLoading, draft, refetch } = useSystem({ id: stationId! });
    const [draftState, setDraftState] = draft;

    const [mutate, { isLoading: mutationLoading }] = useSystemMutation({
        fixedCacheKey: 'SystemMutation',
    });

    // Audit history
    const { data: auditData = [] } = useAuditHistoryQuery({ entityPk: auditPk }, { skip: !stationId });

    // Local state for pending operations
    const [pendingPlanChange, setPendingPlanChange] = useState<{
        targetPlanId: BillingType;
        effectiveDate: string;
        justification?: string;
    } | null>(null);

    const [pendingStationMove, setPendingStationMove] = useState<{
        locationId: number;
        effectiveDate: string;
    } | null>(null);

    const [isMoveDialogOpen, setMoveDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cancelMoveLoading, setCancelMoveLoading] = useState(false);

    const isUnmigrated = !system?.SerialNum;
    const isReassignment =
        system?.LocationID !== draftState?.LocationID ||
        system?.CompanyID !== draftState?.CompanyID ||
        system?.ResellerID !== draftState?.ResellerID;

    const systemDataLoading = systemQueryLoading || mutationLoading;
    const orgDataLoading = nameMapLoading || locationMapLoading;
    const isLoading = systemDataLoading || orgDataLoading;
    const isUnassigned = !draftState?.LocationID;

    // Get company name from assignment
    const companyName = draftState?.CompanyID ? nameMap?.companies?.[draftState.CompanyID] : null;
    const resellerName = draftState?.ResellerID ? nameMap?.resellers?.[draftState.ResellerID] : null;
    const locationName = draftState?.LocationID ? nameMap?.locations?.[draftState.LocationID] : null;

    const handleSave = async () => {
        setSaving(true);
        try {
            // Execute pending plan change
            if (pendingPlanChange) {
                const resp = await requestPlanChange(
                    IdToken!,
                    system!.StationID!,
                    pendingPlanChange.justification || '',
                    pendingPlanChange.effectiveDate,
                    pendingPlanChange.targetPlanId
                ).catch((err) => ({ success: false, message: err?.response?.data?.message }));
                if (!resp.success) throw new Error(resp.message);
            }

            // Execute pending station move
            if (pendingStationMove) {
                const resp = await requestStationMove(
                    IdToken!,
                    system!.StationID!,
                    pendingStationMove.locationId,
                    pendingStationMove.effectiveDate
                ).catch((err) => ({ success: false, message: err?.response?.data?.message }));
                if (!resp.success) throw new Error(resp.message);
            }

            // Prepare system payload
            let systemToSave = { ...draftState } as SystemType;
            if (pendingStationMove) {
                const isImmediate =
                    dayjs(pendingStationMove.effectiveDate).isSame(dayjs(), 'day') ||
                    dayjs(pendingStationMove.effectiveDate).isBefore(dayjs(), 'day');
                if (!isImmediate) {
                    systemToSave = {
                        ...systemToSave,
                        LocationID: system?.LocationID ?? null,
                    } as SystemType;
                }
            }

            // Save system
            await mutate({ system: systemToSave });

            // Clear pending state
            if (pendingPlanChange) setPendingPlanChange(null);
            if (pendingStationMove) setPendingStationMove(null);
        } finally {
            setSaving(false);
        }
    };

    const pageTitle = system?.SerialNum
        ? `${(system?.ModelName !== 'Unknown' && system?.ModelName) || SystemModelMap[system?.Model as any] || 'System'} #${system?.StationID}`
        : 'RapidCut System';

    const lastSeenStatus = system?.LastConnected
        ? `Last seen ${dayjs(system.LastConnected).fromNow()}`
        : 'Never connected';

    return (
        <>
            <SystemEffects onSuccess={onSuccess} />

            <EntityPageLayout loading={isLoading && !system}>
                <OrgChainBreadcrumb
                    ancestors={[
                        system?.CompanyID && companyName
                            ? { type: Selection.Company, id: system.CompanyID, name: companyName }
                            : null,
                        system?.ResellerID && resellerName
                            ? { type: Selection.Reseller, id: system.ResellerID, name: resellerName }
                            : null,
                        system?.LocationID && locationName
                            ? { type: Selection.Location, id: system.LocationID, name: locationName }
                            : null,
                    ]}
                    current={system?.SerialNum ? `System · ${system.SerialNum}` : `System #${stationId ?? ''}`}
                />

                {/* Header */}
                <EntityPageHeader
                    entityType='system'
                    title='RapidCut System'
                    id={system?.StationID ?? undefined}
                    subtitle={lastSeenStatus}
                    icon={<ComputerIcon />}
                    loading={isLoading && !system}
                    onCancel={onSuccess}
                    onSave={role.stores.edit ? handleSave : undefined}
                    saveDisabled={
                        (isReassignment && isUnmigrated) ||
                        !Validate.system(draftState) ||
                        (isUnassigned && !role.prices.edit && !role.isAdmin) ||
                        saving
                    }
                    saving={saving || mutationLoading}
                    actions={
                        <Button
                            variant='secondary'
                            onClick={() =>
                                navigateToActivity('system', system?.StationID || '', {
                                    serialNum: system?.SerialNum || '',
                                })
                            }>
                            View Activity
                        </Button>
                    }
                />

                {/* System Information Card */}
                <InfoCard title='System Information' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2}>
                        <ReadOnlyField label='System ID' value={system?.StationID} size={6} mono copyable />
                        <ReadOnlyField label='Serial Number' value={system?.SerialNum} size={6} mono copyable />
                        <ReadOnlyField
                            label='Model'
                            value={
                                // Server-computed ModelName (MachineVariant-first); legacy maps for older rows
                                (system?.ModelName !== 'Unknown' && system?.ModelName) ||
                                SystemVariantMap[system?.ProductID as any] ||
                                SystemModelMap[system?.Model as any] ||
                                'Unknown'
                            }
                            size={6}
                        />
                        <ReadOnlyField label='App Version' value={system?.AppVersion || '—'} size={6} mono />

                        <DraftSelectField
                            label='Status'
                            stateKey='Status'
                            draft={draft}
                            disabled={operation === Operation.View}
                            options={SYSTEM_STATUS_OPTIONS}
                            size={6}
                        />

                        {/* Billing Plan - using existing component */}
                        <Grid item xs={12} md={6}>
                            {system && (
                                <BillingPlan
                                    disabled={!role.stores.edit}
                                    refetch={refetch}
                                    system={system}
                                    draft={draft}
                                    pendingPlanChange={pendingPlanChange}
                                    onCancelPending={() => setPendingPlanChange(null)}
                                    onSchedulePlanChange={(state) => setPendingPlanChange(state)}
                                />
                            )}
                        </Grid>

                        {/* Super-admins manage these on the Settings page; other editors keep them here */}
                        {!role.isSuperAdmin && (
                            <>
                                <DraftSelectField
                                    label='Warranty Plan'
                                    stateKey='WarrantyPlanID'
                                    draft={draft}
                                    disabled={!role.prices.edit}
                                    options={Object.entries(WARRANTY_PLAN_MAP).map(([key, value]) => ({
                                        label: value,
                                        value: +key,
                                    }))}
                                    size={6}
                                />

                                <DraftSelectField
                                    label='Product Code Entry'
                                    stateKey='ProductCodeEntryEnabled'
                                    draft={draft}
                                    disabled={!role.stores.edit}
                                    options={[
                                        { label: 'Disabled', value: 0 },
                                        { label: 'Enabled', value: 1 },
                                    ]}
                                    size={6}
                                />

                                {/* UI Selection - only for RCMini */}
                                {system?.Model === SystemModel.RCMINI && (
                                    <DraftSelectField
                                        label='UI Selection'
                                        stateKey='UISelectionID'
                                        draft={draft}
                                        disabled={!role.stores.edit}
                                        options={Object.entries(UI_SELECTION_MAP).map(([key, value]) => ({
                                            label: value,
                                            value: +key,
                                        }))}
                                        size={6}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                </InfoCard>

                {/* Super-admin: warranty, UI selection, product code entry, and the org-tier settings live on the Settings page */}
                {operation !== Operation.Create && system?.StationID && (
                    <KioskSettingsSummary stationId={system.StationID} />
                )}

                {/* System Assignment Card */}
                <InfoCard
                    title='System Assignment'
                    icon={<DescriptionOutlinedIcon />}
                    note='Unmigrated RC2/RC3 systems cannot be reassigned. Contact support if you need to transfer an older system.'>
                    <Grid container spacing={2}>
                        {/* Organization Dropdowns */}
                        <Grid item xs={12}>
                            <OrgDropDowns
                                disabled={operation === Operation.View || Boolean(system?.stationMoveRequest)}
                                size={12}
                                draft={draft}
                                options={{
                                    companies: true,
                                    resellers: true,
                                    locations: true,
                                }}
                                onLocationChange={(_, value: any) => {
                                    const { id } = value.pop() ?? {};
                                    const nextLocationId = id !== undefined ? parseInt(id) : null;

                                    if (nextLocationId && nextLocationId !== system?.LocationID) {
                                        setPendingStationMove({
                                            locationId: nextLocationId,
                                            effectiveDate: dayjs().toDate().toISOString(),
                                        });
                                        setMoveDialogOpen(true);
                                    } else {
                                        setPendingStationMove(null);
                                        setMoveDialogOpen(false);
                                    }
                                }}
                            />
                        </Grid>

                        {/* Pending Station Move Notice */}
                        {(pendingStationMove || system?.stationMoveRequest) && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 1,
                                        backgroundColor: colors.warning.light,
                                        border: `1px solid ${colors.warning.main}`,
                                    }}>
                                    <Typography variant='body2' sx={{ flex: 1 }}>
                                        Scheduled move to{' '}
                                        <strong>
                                            {nameMap?.locations?.[
                                                (system?.stationMoveRequest?.LocationID ||
                                                    pendingStationMove?.locationId)!
                                            ] ||
                                                `Location ${(system?.stationMoveRequest?.LocationID ||
                                                    pendingStationMove?.locationId)!}`}
                                        </strong>{' '}
                                        on{' '}
                                        <strong>
                                            {dayjs(
                                                system?.stationMoveRequest?.EffectiveDate ||
                                                    pendingStationMove?.effectiveDate
                                            ).format('YYYY-MM-DD')}
                                        </strong>
                                    </Typography>
                                    <IconButton
                                        size='small'
                                        disabled={cancelMoveLoading}
                                        onClick={async () => {
                                            if (!system?.stationMoveRequest) {
                                                setPendingStationMove(null);
                                                setMoveDialogOpen(false);
                                                setDraftState({
                                                    ...draftState,
                                                    LocationID: (system?.LocationID ?? null) as number | null,
                                                });
                                                return;
                                            }

                                            const moveRequestId = system?.stationMoveRequest?.StationMoveRequestID;
                                            if (!moveRequestId || !IdToken) return;

                                            setCancelMoveLoading(true);
                                            const resp = await cancelStationMove(IdToken, moveRequestId).catch(
                                                (err) => ({
                                                    success: false,
                                                    message: err?.response?.data?.message,
                                                })
                                            );

                                            if (!resp.success) {
                                                setAlert(resp.message || 'Failed to cancel station move', 'error');
                                            } else {
                                                setAlert('Station move request cancelled successfully', 'success');
                                                refetch();
                                            }
                                            setCancelMoveLoading(false);
                                        }}>
                                        <HighlightOff fontSize='small' />
                                    </IconButton>
                                </Box>
                            </Grid>
                        )}

                        {/* Notes (Admin only) */}
                        {role.isAdmin && role.stores.edit && (
                            <DraftTextField
                                label='Internal Notes'
                                stateKey='Notes'
                                draft={draft}
                                multiline
                                rows={4}
                                size={12}
                                helperText={{
                                    valid: 'Only visible to administrators',
                                    invalid: '',
                                }}
                            />
                        )}
                    </Grid>
                </InfoCard>

                {/* System Location Card */}
                <InfoCard title='System Location' icon={<LocationOnIcon />}>
                    <ViewSystemLocation locationId={system?.LocationID} />
                </InfoCard>

                {/* Force Offset (Admin only) */}
                {role.isAdmin && system?.SerialNum && (
                    <ForceOffsetCard
                        serialNumber={system?.SerialNum}
                        productId={system?.ProductID}
                        machineVariant={system?.MachineVariant}
                        stationID={system?.StationID}
                    />
                )}

                {/* Audit Log */}
                {auditData.length > 0 && (
                    <ActivityCard audits={auditData} entityPk={auditPk} maxItems={5} />
                )}
            </EntityPageLayout>

            {/* Station Move Dialog */}
            <StationMoveDialog
                open={isMoveDialogOpen}
                onClose={() => setMoveDialogOpen(false)}
                onConfirm={({ effectiveDateIso }) => {
                    setPendingStationMove((prev) => (prev ? { ...prev, effectiveDate: effectiveDateIso } : prev));
                    setMoveDialogOpen(false);
                }}
                currentLocationLabel={undefined}
                targetLocationLabel={undefined}
            />
        </>
    );
};

export default SystemPanel;
